import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "recent_searches";

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const decodeLegacyString = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.startsWith("{")) {
    return safeParse(trimmed, null);
  }
  try {
    const binary = atob(trimmed);
    if (typeof TextDecoder !== "undefined") {
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      const json = new TextDecoder("utf-8").decode(bytes);
      return safeParse(json, null);
    }
    const json = decodeURIComponent(escape(binary));
    return safeParse(json, null);
  } catch {
    return null;
  }
};

const readList = () => {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  const parsed = safeParse(raw, []);
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === "object") return entry;
      return decodeLegacyString(entry);
    })
    .filter(Boolean);
};

const writeList = (list) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    console.warn("[recent_searches] write", {
      key: STORAGE_KEY,
      count: Array.isArray(list) ? list.length : 0,
      sample: Array.isArray(list) && list[0] ? list[0] : null,
    });
  } catch {
    // ignore storage errors
    console.warn("[recent_searches] write failed", STORAGE_KEY);
  }
};

const buildFallbackValue = (payload) => {
  const parts = [
    payload?.value,
    payload?.label,
    payload?.country,
    payload?.dateRange,
    String(payload?.travelers?.adult ?? 0),
    String(payload?.travelers?.child ?? 0),
    String(payload?.travelers?.baby ?? 0),
  ]
    .filter(Boolean)
    .join("|");
  return parts || "";
};

export const useRecentSearches = ({ max = 6 } = {}) => {
  const [recentSearches, setRecentSearches] = useState(() => {
    return readList();
  });

  const refresh = useCallback(() => {
    setRecentSearches(readList());
  }, []);

  const addRecentSearch = useCallback(
    (payload) => {
      if (!payload) return;
      const safeValue = payload.value || buildFallbackValue(payload);
      if (!safeValue) return;
      const normalized = { ...payload, value: safeValue };
      const existing = readList();
      const next = [
        normalized,
        ...existing.filter((entry) => entry?.value !== safeValue),
      ].slice(0, max);
      writeList(next);
      setRecentSearches(next);
    },
    [max]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      if (event?.key && event.key !== STORAGE_KEY) return;
      refresh();
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [refresh]);

  return { recentSearches, addRecentSearch, refresh };
};
